import { FormControl } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { DefaultFormFields, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms/FormController';
import { dateWithTime } from 'global/utils/dateWithTime';
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { EnrollType } from 'types/enrollmentService';
import { GroupSource } from 'types/groupService';
import { SEUser } from 'types/userService';
import { Cart } from '../../../types/cartService';
import { InputGroup } from '../../../types/groupService';

interface EnrollFields extends DefaultFormFields {
  companyName: FormControl<string>;
  email: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
}

export class EnrollModalStore {
  rootStore: RootStore;
  form: FormController<EnrollFields>;
  loading = false;

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  async saveEnrollModalChanges(cart: Cart, onClose: () => void): Promise<void> {
    this.setLoading(true);
    const { email, firstName, lastName, companyName } = this.form.fields;

    const selfEnrollUser: SEUser = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      userType: 'EMPLOYER',
      groupId: cart.groupId,
    };

    const inputGroup: InputGroup = {
      id: cart.groupId,
      name: companyName.value,
      source: GroupSource.ESTORE,
      effectiveDate: cart.lastUpdateBrowsingCriteria.effectiveDate,
      contactInfo: {
        firstName: firstName.value,
        lastName: lastName.value,
        primaryEmail: email.value,
        primaryAddress: {},
      },
      notes: '',
      agentOfRecord: null,
    };

    const sponsorId = this.rootStore.domainStore.domainHeader.id;
    const inputProductLines = this.rootStore.enrollmentStore.convertNewSaleCartIntoProductLine(cart);
    const cartName = `${companyName.value} ${dateWithTime()}`;

    // Requests
    await this.rootStore.userStore
      .inviteToEStoreEnroll(selfEnrollUser)
      .then(async () => {
        const res = await this.rootStore.cartStore.copyCart(cart.id);
        runInAction(() => {
          const newCart = res.data?.copyCart;
          if (newCart) {
            const requests: unknown[] = [
              this.rootStore.groupStore.updateGroup(inputGroup, sponsorId, newCart.id, true),
              this.rootStore.cartStore.saveProposal(newCart.id, cartName),
            ];

            if (cart.cartItems.length > 0) {
              requests.push(
                this.rootStore.enrollmentStore.saveProposal(
                  cart.groupId,
                  newCart,
                  cartName,
                  inputProductLines,
                  sponsorId,
                  EnrollType.E_STORE_ENROLL,
                ),
              );
            }

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            Promise.all(requests)
              .then(() => {
                this.rootStore.notificationModalStore.openNotificationModal({
                  title: 'Excellent! Check your email inbox.',
                  subtitle:
                    'We have sent you a registration link to create a profile so you can start your enrollment and save your progress.',
                  type: 'ENROLL',
                });
              })
              .catch(() => {
                this.rootStore.notificationModalStore.openNotificationModal({
                  title: 'Something went wrong',
                  subtitle: 'Please try again',
                  type: 'ERROR',
                });
              })
              .finally(() => {
                onClose();
                this.setLoading(false);
                this.form.resetForm();
              });
          }
        });
      })
      .catch(() => {
        this.rootStore.notificationModalStore.openNotificationModal({
          title: 'Something went wrong',
          subtitle: 'Please try again',
          type: 'ERROR',
        });
        this.setLoading(false);
      });
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.form = new FormController({
      companyName: new FormControl<string>(''),
      email: new FormControl<string>('', { validations: ['isEmailValid'] }),
      firstName: new FormControl<string>(''),
      lastName: new FormControl<string>(''),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
